import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

// Redux Core Slice Actions for Session Info
import {
  setCurrentPage,
  setSelectedSessionInfo,
} from "../../redux/admin/sessionInfo.slice";

// Redux Operation Thunks for Session Info
import {
  fetchSessionInfos,
  addSessionInfo,
  updateSessionInfo,
} from "../../redux/admin/sessionInfo.thunk";

// Cross-Slice Dependencies (For fetching base options and reading mapped users)
import { fetchSessionMasters } from "../../redux/admin/sessionMaster.thunk";
import { fetchStudents } from "../../redux/admin/student.thunk";
import { fetchCourseInstructors } from "../../redux/admin/courseInstructor.thunk";
import { fetchBranches } from "../../redux/admin/branch.thunk";

// New Core Slice Dependencies for Books and Chapters
import { fetchBooks, fetchBookChapters } from "../../redux/admin/book.thunk";
import { clearBookChapters, clearBookData } from "../../redux/admin/book.slice";
import {
  selectBooks,
  selectBookChapters,
  selectBookLoading,
} from "../../redux/admin/book.selector";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* ─── Spinner component ─── */
const Spinner = ({ size = 16, color = "#ffffff", thickness = 2 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    style={{ animation: "si-spin 0.72s linear infinite", flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke={color} strokeOpacity="0.22" strokeWidth={thickness} />
    <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth={thickness} strokeLinecap="round" />
  </svg>
);

/* ─── Skeleton shimmer ─── */
const Skeleton = ({ w = "100%", h = 14, r = 6, style: extra = {} }) => (
  <div
    style={{
      width: w, height: h, borderRadius: r,
      background: "linear-gradient(90deg,#f0f0f5 25%,#e8e8ed 50%,#f0f0f5 75%)",
      backgroundSize: "600px 100%",
      animation: "si-shimmer 1.4s infinite linear",
      ...extra,
    }}
  />
);

const SessionInfo = () => {
  const dispatch = useDispatch();
  const limit = 10;

  /* =====================================================
     REDUX SELECTORS
  ====================================================== */
  const sessionInfoState = useSelector((state) => state.admin?.sessionInfo || {});
  const sessionMasterState = useSelector((state) => state.admin?.sessionMaster || {});
  const studentsState = useSelector((state) => state.admin?.students || {});
  const courseInstructorState = useSelector((state) => state.admin?.courseInstructor || {});
  const branchState = useSelector((state) => state.admin?.branches || {});

  // Redux selectors for books and chapters with absolute array safety fallbacks
  const booksList = useSelector(selectBooks) || [];
  const chaptersList = useSelector(selectBookChapters) || [];
  const isBookSliceLoading = useSelector(selectBookLoading);

  const masterSessionsList = sessionMasterState.sessionMasters || [];
  
  // Consistent active student resolution pattern matching StudentPerformance page
  const globalStudents = React.useMemo(() => {
    return studentsState.studentList || studentsState.students || studentsState.data || [];
  }, [studentsState]);

  const globalInstructors = courseInstructorState.courseInstructors || courseInstructorState.instructors || courseInstructorState.data || [];
  const globalBranches = branchState.branches || branchState.branchList || [];

  const {
    sessionInfos = [],
    totalPages = 1,
    currentPage = 1,
    total = 0,
    loading = false,
    selectedSessionInfo = null,
  } = sessionInfoState;

  /* =====================================================
     SEARCH & FILTER STATES
  ====================================================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  /* =====================================================
     MODAL & TABS STATE
  ====================================================== */
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [saving, setSaving] = useState(false);

  /* =====================================================
     INITIAL FORM STATE
  ====================================================== */
  const initialFormState = {
    session_master_id: "",
    instructor_id: "",
    book_id: "",       
    chapter_id: "",    
    session_date: "",
    start_time: "",
    end_time: "",
    topic: "",
    notes: "",
    attendance_editable: 1,
    attendance_roster: [], 
  };

  const [formData, setFormData] = useState(initialFormState);
  const [rosterSearch, setRosterSearch] = useState("");

  /* =====================================================
     SEARCHABLE EXTERNAL STUDENT STATES
  ====================================================== */
  const [externalSearchInput, setExternalSearchInput] = useState("");
  const [showExternalDropdown, setShowExternalDropdown] = useState(false);
  const externalDropdownRef = React.useRef(null);

  // Close external dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (externalDropdownRef.current && !externalDropdownRef.current.contains(event.target)) {
        setShowExternalDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* =====================================================
     SEARCH DEBOUNCE EFFECT
  ====================================================== */
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 600);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  /* =====================================================
     FETCH LIFECYCLE MANAGEMENT (USING ACTIVE STATUS PARAM)
  ====================================================== */
  useEffect(() => {
    dispatch(fetchSessionInfos({
      page: currentPage,
      limit,
      search: debouncedSearch,
    }));
  }, [dispatch, currentPage, debouncedSearch]);

  useEffect(() => {
    dispatch(fetchSessionMasters({ page: 1, limit: 1000 }));
    dispatch(fetchStudents({ page: 1, limit: 1000, search: "", status: "active" }));
    dispatch(fetchBranches({ page: 1, limit: 1000 }));
    dispatch(fetchCourseInstructors({ page: 1, limit: 1000 }));
  }, [dispatch]);

  /* =====================================================
     AUTOMATED STUDENT & INSTRUCTOR AUTO-RESOLVER
  ====================================================== */
  const handleMasterSessionChange = (masterId) => {
    if (!masterId) {
      setFormData((prev) => ({
        ...prev,
        session_master_id: "",
        instructor_id: "",
        book_id: "",
        chapter_id: "",
        attendance_roster: [],
      }));
      dispatch(clearBookData());
      return;
    }

    const targetMasterId = Number(masterId);
    const matchedMaster = masterSessionsList.find((m) => Number(m.id) === targetMasterId);

    if (matchedMaster) {
      const mappedStudentIds = matchedMaster.students ? matchedMaster.students.map(Number) : [];
      const autoInstructorId = matchedMaster.instructor_id ? Number(matchedMaster.instructor_id) : "";
      
      if (matchedMaster.course_id) {
        dispatch(fetchBooks(matchedMaster.course_id));
        dispatch(fetchCourseInstructors(matchedMaster.course_id)); 
      } else {
        dispatch(clearBookData());
      }

      const generatedRoster = mappedStudentIds.map((sid) => {
        const studentObj = globalStudents.find((s) => Number(s.id) === sid);
        return {
          student_id: sid,
          attendance_status: "Absent",
          name: studentObj ? (studentObj.name || studentObj.student_name || studentObj.full_name || "") : "",
          last_name: studentObj ? (studentObj.last_name || "") : "",
          grade: studentObj ? (studentObj.grade || "") : "",
          student_current_status: studentObj ? (studentObj.status || "active") : "active",
          is_external: false,
          is_newly_added: false
        };
      });

      setFormData((prev) => ({
        ...prev,
        session_master_id: targetMasterId,
        instructor_id: autoInstructorId,
        book_id: "",
        chapter_id: "",
        attendance_roster: generatedRoster,
      }));

      toastr.info(
        `Loaded ${generatedRoster.length} students and filtered course dependencies.`
      );
    } else {
      setFormData((prev) => ({ ...prev, session_master_id: targetMasterId }));
    }
  };

  /* =====================================================
     BOOK & CHAPTER DYNAMIC VALUE RESOLVERS
  ====================================================== */
  const handleBookChange = (bookId) => {
    if (!bookId) {
      setFormData((prev) => ({ ...prev, book_id: "", chapter_id: "" }));
      dispatch(clearBookChapters());
      return;
    }

    setFormData((prev) => ({ ...prev, book_id: bookId, chapter_id: "" }));
    dispatch(fetchBookChapters(bookId));
  };

  /* =====================================================
     HIERARCHICAL CHAPTER STRUCTURING HELPER
  ====================================================== */
  const getStructuredChaptersList = () => {
    if (!chaptersList || chaptersList.length === 0) return [];
    
    const mainChapters = chaptersList.filter((c) => !c.parent_chapter_id);
    const subChapters = chaptersList.filter((c) => c.parent_chapter_id);

    mainChapters.sort((a, b) => {
      const numA = parseFloat(a.chapter_no) || 0;
      const numB = parseFloat(b.chapter_no) || 0;
      if (numA !== numB) return numA - numB;
      return String(a.chapter_no || "").localeCompare(String(b.chapter_no || ""));
    });

    const structuredList = [];
    mainChapters.forEach((main) => {
      structuredList.push({ ...main, isSub: false });
      
      const children = subChapters.filter((sub) => String(sub.parent_chapter_id) === String(main.chapter_id));
      children.sort((a, b) => {
        const numA = parseFloat(a.chapter_no) || 0;
        const numB = parseFloat(b.chapter_no) || 0;
        if (numA !== numB) return numA - numB;
        return String(a.chapter_no || "").localeCompare(String(b.chapter_no || ""));
      });
      
      children.forEach((child) => {
        structuredList.push({ ...child, isSub: true });
      });
    });

    const handledIds = new Set(structuredList.map((item) => String(item.chapter_id || item.id)));
    chaptersList.forEach((ch) => {
      const chId = String(ch.chapter_id || ch.id);
      if (!handledIds.has(chId)) {
        structuredList.push({ ...ch, isSub: Boolean(ch.parent_chapter_id) });
      }
    });

    return structuredList;
  };

  const finalStructuredChapters = getStructuredChaptersList();

  /* =====================================================
     HANDLERS & UTILITY INTERFACES
  ====================================================== */
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) dispatch(setCurrentPage(page));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleAttendanceStatus = (studentId, currentStatus) => {
    if (currentStatus && String(currentStatus).toLowerCase() === "inactive") {
      return; 
    }
    setFormData((prev) => {
      const updatedRoster = prev.attendance_roster.map((item) => {
        if (Number(item.student_id) === Number(studentId)) {
          return {
            ...item,
            attendance_status: item.attendance_status === "Present" ? "Absent" : "Present",
          };
        }
        return item;
      });
      return { ...prev, attendance_roster: updatedRoster };
    });
  };

  /* =====================================================
     COMPENSATORY / EXTERNAL STUDENT HANDLERS
  ====================================================== */
  const handleAddExternalStudent = (studentObj) => {
    const targetStudentId = Number(studentObj.id);

    const exists = formData.attendance_roster.some(r => Number(r.student_id) === targetStudentId);
    if (exists) {
      toastr.warning("Student is already present in this session roster.");
      return;
    }

    const sName = studentObj.name || studentObj.student_name || studentObj.full_name || "";
    const newExternalItem = {
      student_id: targetStudentId,
      attendance_status: "Present", 
      name: sName,
      last_name: studentObj.last_name || "",
      grade: studentObj.grade || "",
      student_current_status: studentObj.status || "active",
      is_external: true,
      is_newly_added: true // Flag true so delete button is visible only before saving
    };

    setFormData(prev => ({
      ...prev,
      attendance_roster: [...prev.attendance_roster, newExternalItem]
    }));

    setExternalSearchInput("");
    setShowExternalDropdown(false);
    toastr.success(`Added ${sName} as a compensatory attendee.`);
  };

  const handleRemoveExternalStudent = (studentId) => {
    setFormData(prev => ({
      ...prev,
      attendance_roster: prev.attendance_roster.filter(r => Number(r.student_id) !== Number(studentId))
    }));
    toastr.info("Removed compensatory attendee from session log.");
  };

  const getParentMasterName = (masterId) => {
    const matched = masterSessionsList.find((m) => Number(m.id) === Number(masterId));
    if (!matched) return `Master Schedule #${masterId}`;

    const sessionName = matched.session_name || "";
    const is1to1 = matched.category === "1-1 Session" || matched.session_type === "1-1";
    const isOnline = matched.mode === "online" || String(sessionName).toLowerCase().includes("online");

    if (is1to1 || isOnline) {
      const firstStudentId = matched.students && matched.students[0];
      const studentObj = firstStudentId ? getStudentObject(firstStudentId) : null;
      const studentName = studentObj ? (studentObj.student_name || studentObj.name || studentObj.full_name) : "";
      return studentName ? `${sessionName} - ${studentName}` : sessionName;
    } 
    
    let branchLabel = matched.branch_name || matched.branch || "";
    if (!branchLabel && matched.branch_id) {
      const matchedBranchObj = globalBranches.find((b) => Number(b.id) === Number(matched.branch_id));
      if (matchedBranchObj) {
        branchLabel = matchedBranchObj.branch_name || matchedBranchObj.name || "";
      }
    }
    
    return branchLabel ? `${sessionName} (${branchLabel})` : sessionName;
  };

  const getStudentObject = (studentId) => {
    return globalStudents.find((s) => Number(s.id) === Number(studentId));
  };

  const getInstructorName = (instructorId) => {
    if (!instructorId) return "Unassigned";
    if (!globalInstructors || globalInstructors.length === 0) {
      return `Loading Instructor #${instructorId}...`;
    }

    const matched = globalInstructors.find((i) => {
      const targetId = i.instructor_id ?? i.id ?? i.userId; 
      return Number(targetId) === Number(instructorId);
    });

    if (matched) {
      return matched.full_name || matched.name || matched.instructor_name || `Instructor #${instructorId}`;
    }

    return `Instructor #${instructorId}`;
  };

  const formatDateDisplay = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  };

  /* =====================================================
     MODAL WINDOW TRIGGERS
  ====================================================== */
  const openAddModal = () => {
    setIsEditMode(false);
    setFormData(initialFormState);
    setRosterSearch("");
    setExternalSearchInput("");
    setShowExternalDropdown(false);
    setActiveTab("details");
    dispatch(clearBookData());
    setShowModal(true);
  };

  const openEditModal = (instance) => {
    setIsEditMode(true);
    dispatch(setSelectedSessionInfo(instance));

    const matchedMaster = masterSessionsList.find((m) => Number(m.id) === Number(instance.session_master_id));
    if (matchedMaster && matchedMaster.course_id) {
      dispatch(fetchBooks(matchedMaster.course_id));
      dispatch(fetchCourseInstructors(matchedMaster.course_id)); 
    }
    if (instance.book_id) {
      dispatch(fetchBookChapters(instance.book_id));
    }

    const masterMappedIds = matchedMaster && matchedMaster.students 
      ? matchedMaster.students.map(id => Number(id)) 
      : [];

    let flatRoster = [];
    const rawRoster = instance.attendance_roster;
    if (rawRoster && typeof rawRoster === "object" && !Array.isArray(rawRoster)) {
      const active = rawRoster.active_students || [];
      const inactive = rawRoster.inactive_students || [];
      flatRoster = [...active, ...inactive].map(r => {
        const sId = Number(r.student_id);
        const sObj = getStudentObject(sId);
        const isExt = Boolean(r.is_external) || (masterMappedIds.length > 0 && !masterMappedIds.includes(sId));
        return {
          student_id: sId,
          attendance_status: r.attendance_status || "Absent",
          name: r.name || (sObj ? (sObj.name || sObj.student_name || sObj.full_name) : ""),
          last_name: r.last_name || (sObj ? sObj.last_name : "") || "",
          grade: r.grade || (sObj ? sObj.grade : "") || "",
          student_current_status: r.student_current_status || (sObj ? sObj.status : "active") || "active",
          is_external: isExt,
          is_newly_added: false // Loaded from DB, so no pre-save delete button
        };
      });
    } else if (Array.isArray(rawRoster)) {
      flatRoster = rawRoster.map(r => {
        const sId = Number(r.student_id);
        const sObj = getStudentObject(sId);
        const isExt = Boolean(r.is_external) || (masterMappedIds.length > 0 && !masterMappedIds.includes(sId));
        return {
          student_id: sId,
          attendance_status: r.attendance_status || "Absent",
          name: r.name || (sObj ? (sObj.name || sObj.student_name || sObj.full_name) : ""),
          last_name: r.last_name || (sObj ? sObj.last_name : "") || "",
          grade: r.grade || (sObj ? sObj.grade : "") || "",
          student_current_status: r.student_current_status || (sObj ? sObj.status : "active") || "active",
          is_external: isExt,
          is_newly_added: false
        };
      });
    }

    setFormData({
      session_master_id: instance.session_master_id || "",
      instructor_id: instance.instructor_id || "",
      book_id: instance.book_id || "",
      chapter_id: instance.chapter_id || "",
      session_date: instance.session_date || "",
      start_time: instance.start_time || "",
      end_time: instance.end_time || "",
      topic: instance.topic || "",
      notes: instance.notes || "",
      attendance_editable: instance.attendance_editable ?? 1,
      attendance_roster: flatRoster,
    });
    setRosterSearch("");
    setExternalSearchInput("");
    setShowExternalDropdown(false);
    setActiveTab("details");
    setShowModal(true);
  };

  /* =====================================================
     FORM SUBMISSION LAYOUT
  ====================================================== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.session_master_id || !formData.session_date) {
      toastr.warning("Master reference and date values are required.");
      return;
    }

    setSaving(true);
    try {
      if (isEditMode) {
        await dispatch(updateSessionInfo({ id: selectedSessionInfo.id, sessionInfoData: formData })).unwrap();
        toastr.success("Session data track modified successfully.");
      } else {
        await dispatch(addSessionInfo(formData)).unwrap();
        toastr.success("Session instance cataloged successfully.");
      }
      setShowModal(false);
      dispatch(fetchSessionInfos({ page: currentPage, limit, search: debouncedSearch }));
    } catch (err) {
      toastr.error(err || "Operation flow caught an unhandled exception.");
    } finally {
      setSaving(false);
    }
  };

  const renderPagination = () =>
    Array.from({ length: totalPages }, (_, i) => i + 1).map((i) => (
      <button
        key={i}
        type="button"
        onClick={() => handlePageChange(i)}
        className={`si-page-btn${currentPage === i ? " active" : ""}`}
      >
        {i}
      </button>
    ));

  const totalRosterCount = formData.attendance_roster.length;
  const presentsCount = formData.attendance_roster.filter((r) => r.attendance_status === "Present").length;

  return (
    <div className="si-page">
      <div className="si-wrap">

        {/* ── PAGE HEADER ── */}
        <div className="si-header">
          <div>
            <p className="si-eyebrow">Attendance Logs · Operations Dashboard</p>
            <h1 className="si-title">Session Instances</h1>
          </div>
          <button type="button" className="si-btn-primary" onClick={openAddModal}>
            <i className="fas fa-plus"></i> New Log Instance
          </button>
        </div>

        {/* ── KPI STRIP ── */}
        <div className="si-kpi-grid">
          {[
            { label: "Tracked instances", value: total },
            { label: "Active view entries", value: sessionInfos.length },
            { label: "Pagination scope", value: `${currentPage} / ${totalPages}` },
          ].map(({ label, value }) => (
            <div key={label} className="si-kpi">
              <span className="si-kpi-label">{label}</span>
              <span className="si-kpi-val">{loading ? <Skeleton w={40} h={22} r={5} /> : value}</span>
            </div>
          ))}
        </div>

        {/* ── TOOLBAR ── */}
        <div className="si-toolbar">
          <div className="si-search">
            <i className="fas fa-search si-search-icon"></i>
            <input
              type="text"
              placeholder="Search by topic description, or logs notes…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* ── DATA TABLE CARD ── */}
        <div className="si-card">
          <div className="table-responsive">
            <table className="si-table">
              <thead>
                <tr>
                  <th style={{ width: "32%" }}>Book / Chapter Reference</th>
                  <th style={{ width: "22%" }}>Master Reference Group</th>
                  <th style={{ width: "13%" }}>Instructor</th>
                  <th style={{ width: "11%" }}>Date</th>
                  <th style={{ width: "12%" }}>Roster Allocation</th>
                  <th className="text-end" style={{ width: "10%" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="si-row">
                      <td><Skeleton w="75%" h={13} /><Skeleton w="40%" h={10} style={{ marginTop: 6 }} /></td>
                      <td><Skeleton w="60%" h={12} /></td>
                      <td><Skeleton w="50%" h={12} /></td>
                      <td><Skeleton w="70%" h={12} /></td>
                      <td><Skeleton w="45%" h={20} r={20} /></td>
                      <td style={{ textAlign: "right" }}><Skeleton w={52} h={30} r={7} style={{ marginLeft: "auto" }} /></td>
                    </tr>
                  ))
                ) : sessionInfos.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="si-empty-state">
                      <div className="si-empty-icon-wrap">
                        <i className="fas fa-clipboard-user"></i>
                      </div>
                      <p className="si-empty-title">No log records caught in filter parameters</p>
                      <span className="si-empty-desc">Create a dynamic session tracking instance above to start logging.</span>
                    </td>
                  </tr>
                ) : (
                  sessionInfos.map((instance) => {
                    let allRosterArr = [];
                    if (instance.attendance_roster) {
                      if (Array.isArray(instance.attendance_roster)) {
                        allRosterArr = instance.attendance_roster;
                      } else {
                        const active = instance.attendance_roster.active_students || [];
                        const inactive = instance.attendance_roster.inactive_students || [];
                        allRosterArr = [...active, ...inactive];
                      }
                    }
                    const presents = allRosterArr.filter(r => r.attendance_status === "Present").length;
                    
                    const mainTitle = instance.book_title || instance.topic;
                    const subTitle = instance.book_title ? instance.chapter_name : instance.notes;

                    return (
                      <tr key={instance.id} className="si-row">
                        <td data-label="Book / Chapter Reference">
                          <div className="si-topic-main">
                            {mainTitle || (
                              <span style={{ color: "#aeaeb2", fontWeight: 4, fontStyle: "italic" }}>
                                Untitled Topic
                              </span>
                            )}
                          </div>
                          {subTitle && <div className="si-notes-sub">{subTitle}</div>}
                        </td>
                        <td data-label="Master Reference Group">
                          <span className="si-master-badge">
                            <i className="fas fa-layer-group"></i> {getParentMasterName(instance.session_master_id)}
                          </span>
                        </td>
                        <td data-label="Instructor">
                          <div className="si-table-text">{getInstructorName(instance.instructor_id)}</div>
                        </td>
                        <td data-label="Date">
                          <div className="si-date-main">{formatDateDisplay(instance.session_date)}</div>
                          <div className="si-notes-sub">
                            {instance.start_time ? instance.start_time.substring(0, 5) : "—"} - {instance.end_time ? instance.end_time.substring(0, 5) : "—"}
                          </div>
                        </td>
                        <td data-label="Roster Allocation">
                          <span className={`si-pill ${presents > 0 ? "pill-blue" : "pill-amber"}`}>
                            {presents} Present / {allRosterArr.length} Total
                          </span>
                        </td>
                        <td className="text-end" data-label="Actions">
                          <button type="button" className="si-btn-outline" onClick={() => openEditModal(instance)}>
                            View / Log
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ── PAGINATION PANEL ── */}
          <div className="si-footer">
            <span className="si-count">{sessionInfos.length} of {total} records</span>
            <div className="si-pagination">
              <button type="button" className="si-page-btn si-page-arrow" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                <i className="fas fa-chevron-left"></i>
              </button>
              {renderPagination()}
              <button type="button" className="si-page-btn si-page-arrow" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════
          MODAL INTERACTION DIALOGUE
      ═══════════════════════════════════════ */}
      {showModal && (
        <div className="si-modal-overlay" onClick={(e) => e.target === e.currentTarget && !saving && setShowModal(false)}>
          <div className="si-modal">

            {/* Sidebar Navigation */}
            <div className="si-modal-side">
              <div className="si-modal-side-header">
                <p className="si-modal-side-title">Instance Hub</p>
                <p className="si-modal-side-sub">Log Profile Controls</p>
              </div>
              <nav className="si-modal-nav">
                {[
                  { key: "details", icon: "fas fa-sliders-h", label: "Metadata core" },
                  { key: "roster", icon: "fas fa-user-check", label: "Attendance roster" },
                ].map(({ key, icon, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`si-nav-item${activeTab === key ? " active" : ""}`}
                    onClick={() => setActiveTab(key)}
                    disabled={saving}
                  >
                    <i className={icon}></i>
                    <span>{label}</span>
                    {key === "roster" && totalRosterCount > 0 && (
                      <span className="si-nav-badge">{presentsCount}/{totalRosterCount}</span>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Main Form Working Panel */}
            <form onSubmit={handleSubmit} className="si-modal-main">
              <div className="si-modal-header">
                <div>
                  <h2 className="si-modal-title">{isEditMode ? "Modify logged transaction" : "Create class logging instance"}</h2>
                  <p className="si-modal-sub">
                    {isEditMode ? "Commit updates over the configuration metrics below." : "Selecting a master group automatically resolves structural mappings."}
                  </p>
                </div>
                <button type="button" className="btn-close shadow-none" onClick={() => setShowModal(false)} aria-label="Close" disabled={saving}></button>
              </div>

              <div className="si-modal-body">
                <div className="row g-3">

                  {/* TAB: BASE SYSTEM DETAILS */}
                  {activeTab === "details" && (
                    <>
                      <div className="col-12 col-md-12">
                        <label className="si-label">Parent Master Reference *</label>
                        <select
                          name="session_master_id"
                          className="si-field"
                          value={formData.session_master_id}
                          onChange={(e) => handleMasterSessionChange(e.target.value)}
                          disabled={isEditMode || saving}
                        >
                          <option value="">Select configuration group profile</option>
                          {masterSessionsList.map((m) => (
                            <option key={m.id} value={m.id}>{getParentMasterName(m.id)}</option>
                          ))}
                        </select>
                      </div>

                      <div className="col-12 col-md-12">
                        <label className="si-label">Assigned Instructor Override</label>
                        <select
                          name="instructor_id"
                          className="si-field"
                          value={formData.instructor_id}
                          onChange={handleChange}
                          disabled={saving || !formData.session_master_id}
                        >
                          <option value="">
                            {globalInstructors.length === 0 ? "No instructors mapped to this course context" : "Select designated trainer profile"}
                          </option>
                          {globalInstructors.map((coach) => {
                            const cid = Number(coach.instructor_id || coach.id);
                            return <option key={cid} value={cid}>{coach.full_name || coach.name}</option>;
                          })}
                        </select>
                      </div>

                      <div className="col-12 col-md-12">
                        <label className="si-label">Lesson Topic title</label>
                        <input type="text" name="topic" className="si-field" placeholder="e.g. Opening tactics or tournament practice match review (Optional)" value={formData.topic} onChange={handleChange} disabled={saving} />
                      </div>

                      {/* ── COURSE BOOK & CHAPTER SELECTION REGION ── */}
                      <div className="col-12 col-md-6">
                        <label className="si-label">
                          Associated Course Book {isBookSliceLoading && " (Loading...)"}
                        </label>
                        <select
                          name="book_id"
                          className="si-field"
                          value={formData.book_id}
                          onChange={(e) => handleBookChange(e.target.value)}
                          disabled={saving || !formData.session_master_id || booksList.length === 0}
                        >
                          <option value="">
                            {booksList.length === 0 ? "No structural books available" : "Select assigned textbook (Optional)"}
                          </option>
                          {booksList.map((book, idx) => {
                            const bId = book.book_id || book.id;
                            const bName = book.title || book.book_name || book.name || `Book #${bId || idx}`;
                            return (
                              <option key={bId || idx} value={bId}>
                                {bName}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="si-label">Target Chapter Unit</label>
                        <select
                          name="chapter_id"
                          className="si-field fw-500"
                          value={formData.chapter_id}
                          onChange={handleChange}
                          disabled={saving || !formData.book_id || chaptersList.length === 0}
                        >
                          <option value="">
                            {chaptersList.length === 0 ? "No specific chapters found" : "Select targeted chapter (Optional)"}
                          </option>
                          {finalStructuredChapters.map((chap, idx) => {
                            const cId = chap.chapter_id || chap.id;
                            const isSub = chap.isSub;
                            const prefix = isSub ? `↳  ${chap.chapter_no}. ` : `Chapter ${chap.chapter_no}: `;
                            const cName = `${prefix}${chap.chapter_name || chap.title || chap.name || ""}`;
                            
                            return (
                              <option key={cId || idx} value={cId}>
                                {cName}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="si-label">Target Session Date *</label>
                        <input type="date" name="session_date" className="si-field" value={formData.session_date} onChange={handleChange} disabled={saving} />
                      </div>

                      <div className="col-6 col-md-3">
                        <label className="si-label">Start time</label>
                        <input type="time" name="start_time" className="si-field" value={formData.start_time} onChange={handleChange} disabled={saving} />
                      </div>

                      <div className="col-6 col-md-3">
                        <label className="si-label">End time</label>
                        <input type="time" name="end_time" className="si-field" value={formData.end_time} onChange={handleChange} disabled={saving} />
                      </div>

                      <div className="col-12 col-md-12">
                        <label className="si-label">Administrative log notes</label>
                        <textarea name="notes" rows="3" className="si-field" style={{ resize: "none" }} placeholder="Optional review summaries or homework tracking logs..." value={formData.notes} onChange={handleChange} disabled={saving}></textarea>
                      </div>

                      <div className="col-12 col-md-12">
                        <div className="si-toggle-row">
                          <div>
                            <label className="si-label" style={{ marginBottom: 2 }}>Lock attendance matrix adjustments</label>
                            <span className="si-toggle-sub">Locks editing operations for secondary user validation profiles.</span>
                          </div>
                          <div className="form-check form-switch m-0">
                            <input
                              className="form-check-input si-switch"
                              type="checkbox"
                              checked={Number(formData.attendance_editable) === 1}
                              onChange={(e) => setFormData(prev => ({ ...prev, attendance_editable: e.target.checked ? 1 : 0 }))}
                              disabled={saving}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* TAB: ATTENDANCE TRACKING CHECKLIST MATRIX */}
                  {activeTab === "roster" && (
                    <>
                      <div className="col-12 col-md-12">
                        <div className="si-section-note">
                          Clicking active rows switches student state marks between <b>Present</b> and <b>Absent</b> indices. Inactive students are locked for historical preservation.
                        </div>
                      </div>

                      {/* ── SEARCHABLE EXTERNAL / COMPENSATORY ADDITION REGION ── */}
                      <div className="col-12 col-md-12">
                        <div className="si-external-box">
                          <label className="si-label" style={{ color: "#1e40af", marginBottom: 6 }}>
                            <i className="fas fa-user-plus" style={{ marginRight: 6 }}></i> Add Makeup / Cross-Batch Attendee
                          </label>
                          <div className="si-searchable-dropdown-wrap" ref={externalDropdownRef}>
                            <div className="si-search-input-container">
                              <i className="fas fa-search si-external-search-icon"></i>
                              <input
                                type="text"
                                className="si-field"
                                style={{ paddingLeft: "34px" }}
                                placeholder={!formData.session_master_id ? "Select a parent master group first..." : "Type active student name to search..."}
                                value={externalSearchInput}
                                onChange={(e) => {
                                  setExternalSearchInput(e.target.value);
                                  setShowExternalDropdown(true);
                                }}
                                onFocus={() => formData.session_master_id && setShowExternalDropdown(true)}
                                disabled={saving || !formData.session_master_id}
                              />
                            </div>

                            {showExternalDropdown && formData.session_master_id && (
                              <div className="si-external-dropdown-list">
                                {(() => {
                                  // Filter strictly for active students not already in the roster
                                  const availableActiveStudents = globalStudents.filter((student) => {
                                    const status = String(student.status || "active").toLowerCase();
                                    if (status !== "active") return false; // Only active students

                                    const sId = Number(student.id);
                                    const alreadyInRoster = formData.attendance_roster.some(r => Number(r.student_id) === sId);
                                    if (alreadyInRoster) return false; // Exclude already added

                                    const name = (student.name || student.student_name || student.full_name || "").toLowerCase();
                                    return name.includes(externalSearchInput.toLowerCase());
                                  });

                                  if (availableActiveStudents.length === 0) {
                                    return (
                                      <div className="si-external-dropdown-empty">
                                        No active students found matching "{externalSearchInput}"
                                      </div>
                                    );
                                  }

                                  return availableActiveStudents.map((student) => {
                                    const sName = student.name || student.student_name || student.full_name || `Student #${student.id}`;
                                    const sGrade = student.grade ? ` • Grade: ${student.grade}` : "";
                                    return (
                                      <div
                                        key={student.id}
                                        className="si-external-dropdown-item"
                                        onClick={() => handleAddExternalStudent(student)}
                                      >
                                        <div className="si-dropdown-item-name">{sName}</div>
                                        <div className="si-dropdown-item-sub">{sGrade}</div>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-12">
                        <input
                          type="text"
                          className="si-field"
                          placeholder="Filter current instance attendee list..."
                          value={rosterSearch}
                          onChange={(e) => setRosterSearch(e.target.value)}
                          style={{ marginBottom: 12 }}
                          disabled={saving}
                        />

                        <div className="si-roster-scroll">
                          {formData.attendance_roster.length === 0 ? (
                            <div className="si-roster-empty">
                              <i className="fas fa-users-slash"></i>
                              No students are currently bound to this instance. Select a Master Reference Group profile to resolve roster tracking arrays.
                            </div>
                          ) : (
                            <div className="si-roster-list">
                              {/* Separate and render active vs inactive students visually */}
                              {(() => {
                                const activeList = formData.attendance_roster.filter(item => {
                                  const status = item.student_current_status || (getStudentObject(item.student_id)?.status) || "active";
                                  return String(status).toLowerCase() === "active";
                                });
                                const inactiveList = formData.attendance_roster.filter(item => {
                                  const status = item.student_current_status || (getStudentObject(item.student_id)?.status) || "active";
                                  return String(status).toLowerCase() !== "active";
                                });

                                const filterFn = (item) => {
                                  const sObj = getStudentObject(item.student_id);
                                  const name = item.name || (sObj ? (sObj.student_name || sObj.name || sObj.full_name || "") : "");
                                  return name.toLowerCase().includes(rosterSearch.toLowerCase());
                                };

                                const filteredActive = activeList.filter(filterFn);
                                const filteredInactive = inactiveList.filter(filterFn);

                                // Split active list into batch members vs external/makeup attendees
                                const batchActiveList = filteredActive.filter(item => !item.is_external);
                                const externalActiveList = filteredActive.filter(item => item.is_external);

                                return (
                                  <>
                                    {/* Standard Batch Attendees */}
                                    {batchActiveList.map((item, idx) => {
                                      const sObj = getStudentObject(item.student_id);
                                      const isPresent = item.attendance_status === "Present";
                                      const studentName = item.name || (sObj ? (sObj.student_name || sObj.name || sObj.full_name) : `Student ID #${item.student_id}`);
                                      const gradeVal = item.grade !== undefined && item.grade !== "" ? item.grade : (sObj ? sObj.grade : "N/A");

                                      return (
                                        <div
                                          key={`active-${item.student_id}-${idx}`}
                                          className={`si-roster-row${isPresent ? " present-row" : ""}`}
                                          onClick={() => !saving && toggleAttendanceStatus(item.student_id, item.student_current_status)}
                                        >
                                          <div className="si-roster-left">
                                            <div className={`si-roster-initial ${isPresent ? "bg-blue" : "bg-gray"}`}>
                                              {(studentName[0] || "?").toUpperCase()}
                                            </div>
                                            <div>
                                              <div className="si-roster-name">{studentName}</div>
                                              <div className="si-roster-sub">Grade: {gradeVal || "N/A"}</div>
                                            </div>
                                          </div>
                                          <span className={`si-badge-btn ${isPresent ? "btn-present" : "btn-absent"}`}>
                                            <i className={isPresent ? "fas fa-check-circle" : "fas fa-circle-xmark"}></i>
                                            {item.attendance_status.toUpperCase()}
                                          </span>
                                        </div>
                                      );
                                    })}

                                    {/* External / Compensatory Section */}
                                    {externalActiveList.length > 0 && (
                                      <>
                                        <div style={{ fontSize: "11px", fontWeight: "600", color: "#1e4ed8", textTransform: "uppercase", letterSpacing: "0.05em", margin: "14px 0 6px 2px" }}>
                                          Compensatory / Cross-Batch Attendees
                                        </div>
                                        {externalActiveList.map((item, idx) => {
                                          const sObj = getStudentObject(item.student_id);
                                          const isPresent = item.attendance_status === "Present";
                                          const studentName = item.name || (sObj ? (sObj.student_name || sObj.name || sObj.full_name) : `Student ID #${item.student_id}`);
                                          const gradeVal = item.grade !== undefined && item.grade !== "" ? item.grade : (sObj ? sObj.grade : "N/A");

                                          return (
                                            <div
                                              key={`external-${item.student_id}-${idx}`}
                                              className={`si-roster-row external-row${isPresent ? " present-row" : ""}`}
                                              style={{ borderLeft: "3px solid #0071e3" }}
                                            >
                                              <div 
                                                className="si-roster-left" 
                                                style={{ flex: 1, cursor: "pointer" }}
                                                onClick={() => !saving && toggleAttendanceStatus(item.student_id, item.student_current_status)}
                                              >
                                                <div className={`si-roster-initial ${isPresent ? "bg-blue" : "bg-gray"}`}>
                                                  {(studentName[0] || "?").toUpperCase()}
                                                </div>
                                                <div>
                                                  <div className="si-roster-name">
                                                    {studentName} 
                                                    <span style={{ fontSize: "10px", fontWeight: 600, color: "#1e40af", background: "#dbeafe", padding: "1px 6px", borderRadius: "4px", marginLeft: "6px" }}>MAKEUP</span>
                                                  </div>
                                                  <div className="si-roster-sub">Grade: {gradeVal || "N/A"}</div>
                                                </div>
                                              </div>
                                              
                                              <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                                                <span 
                                                  className={`si-badge-btn ${isPresent ? "btn-present" : "btn-absent"}`}
                                                  onClick={() => !saving && toggleAttendanceStatus(item.student_id, item.student_current_status)}
                                                  style={{ cursor: "pointer" }}
                                                >
                                                  <i className={isPresent ? "fas fa-check-circle" : "fas fa-circle-xmark"}></i>
                                                  {item.attendance_status.toUpperCase()}
                                                </span>

                                                {/* Show delete trash button ONLY before saving (i.e. is_newly_added is true) */}
                                                {item.is_newly_added && (
                                                  <button
                                                    type="button"
                                                    className="si-btn-outline"
                                                    style={{ padding: "4px 8px", fontSize: "11px", color: "#dc2626", borderColor: "#fecaca" }}
                                                    onClick={() => handleRemoveExternalStudent(item.student_id)}
                                                    title="Remove newly added attendee before saving"
                                                    disabled={saving}
                                                  >
                                                    <i className="fas fa-trash-alt"></i>
                                                  </button>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </>
                                    )}

                                    {filteredInactive.length > 0 && (
                                      <>
                                        <div style={{ fontSize: "11px", fontWeight: "600", color: "#8e8e93", textTransform: "uppercase", letterSpacing: "0.05em", margin: "14px 0 6px 2px" }}>
                                          Inactive / Archived Attendees (Historical Records)
                                        </div>
                                        {filteredInactive.map((item, idx) => {
                                          const sObj = getStudentObject(item.student_id);
                                          const isPresent = item.attendance_status === "Present";
                                          const studentName = item.name || (sObj ? (sObj.student_name || sObj.name || sObj.full_name) : `Student ID #${item.student_id}`);
                                          const gradeVal = item.grade !== undefined && item.grade !== "" ? item.grade : (sObj ? sObj.grade : "N/A");

                                          return (
                                            <div
                                              key={`inactive-${item.student_id}-${idx}`}
                                              className="si-roster-row inactive-row"
                                              style={{ opacity: 0.85, background: "#fafafa", cursor: "default" }}
                                              title="This student is inactive. Historical attendance status cannot be modified."
                                            >
                                              <div className="si-roster-left">
                                                <div className="si-roster-initial bg-gray" style={{ background: "#e8e8ed", color: "#6e6e73" }}>
                                                  {(studentName[0] || "?").toUpperCase()}
                                                </div>
                                                <div>
                                                  <div className="si-roster-name" style={{ color: "#6e6e73" }}>
                                                    {studentName} <span style={{ fontSize: "10px", fontWeight: 600, color: "#d97706", background: "#fef3c7", padding: "1px 6px", borderRadius: "4px", marginLeft: "6px" }}>INACTIVE</span>
                                                  </div>
                                                  <div className="si-roster-sub">Grade: {gradeVal || "N/A"}</div>
                                                </div>
                                              </div>
                                              <span className={`si-badge-btn ${isPresent ? "btn-present" : "btn-absent"}`} style={{ cursor: "not-allowed" }}>
                                                <i className={isPresent ? "fas fa-check-circle" : "fas fa-circle-xmark"}></i>
                                                {item.attendance_status.toUpperCase()}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                </div>
              </div>

              {/* ── MODAL FOOTER ACTION CONTROLS ── */}
              <div className="si-modal-footer">
                <button type="button" className="si-btn-cancel" onClick={() => setShowModal(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="si-btn-primary si-btn-submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size={15} color="#ffffff" thickness={2} />
                      {isEditMode ? "Processing variations..." : "Cataloging..."}
                    </>
                  ) : (
                    isEditMode ? "Save Changes" : "Create Instance"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── EXPLICIT LOCAL STYLES ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,400;0,14..32,500;0,14..32,600;1,14..32,400&display=swap');

        @keyframes si-spin { to { transform: rotate(360deg); } }
        @keyframes si-shimmer {
          0%   { background-position: -600px 0; }
          100% { background-position:  600px 0; }
        }
        @keyframes si-modal-in {
          from { opacity: 0; transform: scale(0.97) translateY(10px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);     }
        }
        @keyframes si-overlay-in { from { opacity: 0; } to { opacity: 1; } }

        .si-page {
          background: #f5f5f7;
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          color: #1d1d1f;
          font-size: 14px;
        }
        .si-wrap { max-width: 1200px; margin: 0 auto; padding: 2rem 1.5rem; }

        .si-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.5rem; }
        .si-eyebrow { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: #aeaeb2; margin: 0 0 4px; }
        .si-title { font-size: 1.35rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.022em; margin: 0; }

        .si-kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 1.25rem; }
        .si-kpi { background: #fff; border: 1px solid #d2d2d7; border-radius: 12px; padding: 14px 18px; display: flex; flex-direction: column; gap: 5px; }
        .si-kpi-label { font-size: 11px; color: #6e6e73; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; }
        .si-kpi-val { font-size: 1.5rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.025em; font-variant-numeric: tabular-nums; min-height: 1.8rem; display: flex; align-items: center; }

        .si-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 1rem; }
        .si-search { flex: 1; position: relative; }
        .si-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #aeaeb2; font-size: 12px; }
        .si-search input {
          width: 100%; background: #fff; border: 1px solid #d2d2d7; border-radius: 10px;
          padding: 9px 12px 9px 34px; font-size: 13px; color: #1d1d1f; outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .si-search input:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }

        .si-btn-primary {
          background: #0071e3; color: #fff; border: none; border-radius: 10px; padding: 9px 18px;
          font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 7px;
          transition: all 0.15s; white-space: nowrap; min-width: 140px; justify-content: center;
        }
        .si-btn-primary:hover:not(:disabled) { background: #0077ed; }
        .si-btn-primary:disabled { opacity: 0.65; cursor: not-allowed; }
        .si-btn-submit { padding: 9px 22px; min-width: 158px; }

        .si-card { background: #fff; border: 1px solid #d2d2d7; border-radius: 14px; overflow: hidden; }
        .si-table { width: 100%; border-collapse: collapse; }
        .si-table thead th {
          background: #f5f5f7; font-size: 10.5px; font-weight: 600; color: #6e6e73;
          text-transform: uppercase; letter-spacing: 0.07em; padding: 11px 18px; border-bottom: 1px solid #e8e8ed;
        }
        .si-table tbody tr { border-bottom: 1px solid #f5f5f7; transition: background 0.1s; }
        .si-row:hover { background: #fafafa; }
        .si-table td { padding: 13px 18px; vertical-align: middle; }

        .si-topic-main { font-size: 13px; font-weight: 600; color: #1d1d1f; }
        .si-notes-sub { font-size: 11.5px; color: #8e8e93; margin-top: 2px; }
        .si-table-text { font-size: 12.5px; font-weight: 500; color: #3c3c43; }
        .si-master-badge { display: inline-flex; align-items: center; gap: 5px; background: #f5f5f7; border: 1px solid #e8e8ed; padding: 4px 10px; border-radius: 6px; font-size: 12px; font-weight: 500; color: #1d1d1f; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .si-master-badge i { color: #8e8e93; font-size: 11px; }
        .si-date-main { font-size: 12.5px; font-weight: 500; color: #1d1d1f; font-variant-numeric: tabular-nums; }

        .si-pill { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 500; white-space: nowrap; }
        .pill-amber { background: #fef3c7; color: #92400e; border: 1px solid #fde68a; }
        .pill-blue  { background: #dbeafe; color: #1e40af; border: 1px solid #bfdbfe; }

        .si-btn-outline {
          border: 1px solid #d2d2d7; background: #fff; border-radius: 8px; padding: 6px 14px;
          font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer; transition: all 0.12s;
        }
        .si-btn-outline:hover { border-color: #aeaeb2; background: #f5f5f7; color: #1d1d1f; }

        .si-empty-state { text-align: center; padding: 3rem 1rem; }
        .si-empty-icon-wrap { width: 52px; height: 52px; background: #f5f5f7; border-radius: 14px; display: inline-flex; align-items: center; justify-content: center; font-size: 1.4rem; color: #aeaeb2; margin-bottom: 12px; }
        .si-empty-title { font-size: 14px; font-weight: 500; color: #3c3c43; margin: 0 0 4px; }
        .si-empty-desc { font-size: 12px; color: #aeaeb2; }

        .si-footer { display: flex; align-items: center; justify-content: space-between; padding: 12px 18px; border-top: 1px solid #f5f5f7; gap: 10px; flex-wrap: wrap; }
        .si-count { font-size: 12px; color: #6e6e73; font-weight: 500; }
        .si-pagination { display: flex; gap: 4px; }
        .si-page-btn { min-width: 30px; height: 30px; padding: 0 6px; border: 1px solid #e8e8ed; background: #fff; border-radius: 7px; font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; transition: all 0.12s; }
        .si-page-btn:hover:not(:disabled):not(.active) { background: #f5f5f7; border-color: #d2d2d7; }
        .si-page-btn.active { background: #0071e3; color: #fff; border-color: #0071e3; }
        .si-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

        .si-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.36); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 16px; animation: si-overlay-in 0.2s ease; }
        .si-modal { width: 100%; max-width: 900px; height: 590px; background: #fff; border-radius: 18px; border: 1px solid #d2d2d7; box-shadow: 0 32px 60px -12px rgba(0,0,0,0.18); display: flex; overflow: hidden; animation: si-modal-in 0.24s cubic-bezier(0.34,1.1,0.64,1); }

        .si-modal-side { width: 210px; background: #f5f5f7; border-right: 1px solid #e8e8ed; display: flex; flex-direction: column; flex-shrink: 0; }
        .si-modal-side-header { padding: 18px 16px; border-bottom: 1px solid #e8e8ed; }
        .si-modal-side-title { font-size: 13px; font-weight: 600; color: #1d1d1f; margin: 0; }
        .si-modal-side-sub { font-size: 11px; color: #aeaeb2; margin: 3px 0 0; }
        .si-modal-nav { padding: 8px; display: flex; flex-direction: column; gap: 2px; }
        .si-nav-item { display: flex; align-items: center; gap: 9px; padding: 9px 12px; border-radius: 9px; border: none; background: transparent; font-size: 13px; color: #6e6e73; cursor: pointer; text-align: left; width: 100%; transition: all 0.12s; position: relative; }
        .si-nav-item i { font-size: 12.5px; width: 14px; text-align: center; }
        .si-nav-item span { flex: 1; }
        .si-nav-item:hover:not(.active) { background: #ebebf0; color: #3c3c43; }
        .si-nav-item.active { background: #fff; color: #0071e3; font-weight: 500; border: 1px solid #e8e8ed; }
        .si-nav-badge { background: #0071e3; color: #fff; font-size: 10px; font-weight: 600; border-radius: 20px; padding: 1px 6px; flex-shrink: 0; }

        .si-modal-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
        .si-modal-header { padding: 18px 26px; border-bottom: 1px solid #f5f5f7; display: flex; align-items: flex-start; justify-content: space-between; }
        .si-modal-title { font-size: 15px; font-weight: 600; color: #1d1d1f; margin: 0; }
        .si-modal-sub { font-size: 12px; color: #6e6e73; margin: 3px 0 0; }
        .si-modal-body { flex: 1; overflow-y: auto; padding: 22px 26px; }
        .si-modal-footer { padding: 14px 26px; border-top: 1px solid #f5f5f7; display: flex; justify-content: flex-end; align-items: center; gap: 10px; background: #f5f5f7; }

        .si-label { display: block; font-size: 11px; font-weight: 600; color: #6e6e73; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 5px; }
        .si-field { width: 100%; border: 1px solid #d2d2d7; border-radius: 9px; padding: 9px 12px; font-size: 13px; color: #1d1d1f; background: #fff; outline: none; transition: all 0.15s; font-family: inherit; }
        .si-field:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }
        .si-field:disabled { background: #f5f5f7; color: #8e8e93; cursor: not-allowed; }
        .si-section-note { background: #f5f5f7; border: 1px solid #e8e8ed; border-radius: 9px; padding: 10px 14px; font-size: 12.5px; color: #6e6e73; line-height: 1.6; }

        .si-external-box { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; padding: 12px 16px; margin-bottom: 4px; }
        .si-searchable-dropdown-wrap { position: relative; }
        .si-search-input-container { position: relative; }
        .si-external-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #8e8e93; font-size: 12px; }
        
        .si-external-dropdown-list {
          position: absolute; top: calc(100% + 4px); left: 0; right: 0; max-height: 200px; overflow-y: auto;
          background: #fff; border: 1px solid #d2d2d7; border-radius: 9px; box-shadow: 0 8px 20px rgba(0,0,0,0.1);
          z-index: 100; padding: 4px;
        }
        .si-external-dropdown-list::-webkit-scrollbar { width: 4px; }
        .si-external-dropdown-list::-webkit-scrollbar-thumb { background: #d2d2d7; border-radius: 3px; }
        .si-external-dropdown-item {
          padding: 8px 12px; border-radius: 6px; cursor: pointer; transition: background 0.1s;
          display: flex; justify-content: space-between; align-items: center;
        }
        .si-external-dropdown-item:hover { background: #f0f6ff; }
        .si-dropdown-item-name { font-size: 12.5px; font-weight: 500; color: #1d1d1f; }
        .si-dropdown-item-sub { font-size: 11px; color: #8e8e93; }
        .si-external-dropdown-empty { padding: 12px; text-align: center; font-size: 12px; color: #8e8e93; }

        .si-toggle-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; background: #f5f5f7; border: 1px solid #e8e8ed; border-radius: 9px; padding: 12px 16px; }
        .si-toggle-sub { font-size: 11.5px; color: #aeaeb2; }
        .si-switch { width: 2.4em; height: 1.2em; cursor: pointer; }

        .si-roster-scroll { max-height: 260px; overflow-y: auto; padding-right: 4px; }
        .si-roster-scroll::-webkit-scrollbar { width: 4px; }
        .si-roster-scroll::-webkit-scrollbar-thumb { background: #d2d2d7; border-radius: 3px; }
        
        .si-roster-list { display: flex; flex-direction: column; gap: 6px; }
        .si-roster-row {
          display: flex; align-items: center; justify-content: space-between; padding: 10px 14px;
          border: 1px solid #e8e8ed; border-radius: 10px; background: #fff; cursor: pointer;
          transition: all 0.12s; user-select: none;
        }
        .si-roster-row:hover:not(.inactive-row) { border-color: #d2d2d7; background: #fafafa; }
        .si-roster-row.present-row { border-color: #bfdbfe; background: #f0f6ff; }
        
        .si-roster-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
        .si-roster-initial {
          width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
          font-weight: 600; font-size: 13px; transition: all 0.12s; flex-shrink: 0;
        }
        .bg-gray { background: #f5f5f7; color: #3c3c43; }
        .bg-blue { background: #0071e3; color: #fff; }
        
        .si-roster-name { font-weight: 500; font-size: 13px; color: #1d1d1f; }
        .si-roster-sub { font-size: 11px; color: #8e8e93; margin-top: 1px; }
        
        .si-badge-btn {
          display: inline-flex; align-items: center; gap: 5px; padding: 5px 12px; border-radius: 20px;
          font-size: 11px; font-weight: 600; letter-spacing: 0.02em; transition: all 0.12s;
        }
        .btn-absent { background: #fff; border: 1px solid #d2d2d7; color: #6e6e73; }
        .btn-present { background: #0071e3; border: 1px solid #0071e3; color: #fff; }
        .si-badge-btn i { font-size: 12px; }

        .si-roster-empty { padding: 2.5rem 1.5rem; text-align: center; color: #6e6e73; font-size: 13px; line-height: 1.6; }
        .si-roster-empty i { font-size: 1.4rem; color: #d2d2d7; margin-bottom: 10px; display: block; }
        
        .si-btn-cancel { border: 1px solid #d2d2d7; background: #fff; border-radius: 9px; padding: 9px 18px; font-size: 13px; font-weight: 500; color: #6e6e73; cursor: pointer; transition: all 0.12s; }
        .si-btn-cancel:hover { background: #f5f5f7; color: #3c3c43; }

        @media (max-width: 991px) {
          .si-kpi-grid { grid-template-columns: repeat(2, 1fr); }
        }

        @media (max-width: 768px) {
          .si-wrap { padding: 1.25rem 1rem; }
          .si-kpi-grid { grid-template-columns: 1fr; }
          
          .si-table thead { display: none; }
          .si-table tbody tr {
            display: block;
            border: 1px solid #e8e8ed;
            border-radius: 10px;
            margin-bottom: 12px;
            padding: 8px 14px;
            background: #fff;
          }
          .si-table td {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 0;
            border: none;
            width: 100% !important;
            text-align: left !important;
          }
          .si-table td::before {
            content: attr(data-label);
            font-size: 11px;
            font-weight: 600;
            color: #8e8e93;
            text-transform: uppercase;
            letter-spacing: 0.03em;
            flex-shrink: 0;
            margin-right: 15px;
          }
          .si-table td.text-end, .si-table td[data-label="Actions"] {
            justify-content: flex-end;
          }
          .si-table td::before { display: inline-block; }
          .si-table td.text-end::before { display: none; }
          
          .si-modal-overlay { padding: 0; }
          .si-modal {
            flex-direction: column;
            height: 100vh;
            max-height: 100vh;
            width: 100vw;
            max-width: 100vw;
            border-radius: 0;
            border: none;
            animation: si-overlay-in 0.2s ease;
          }
          .si-modal-side {
            width: 100%;
            border-right: none;
            border-bottom: 1px solid #e8e8ed;
          }
          .si-modal-side-header { display: none; }
          .si-modal-nav {
            flex-direction: row;
            overflow-x: auto;
            padding: 8px;
            gap: 6px;
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch;
          }
          .si-modal-nav::-webkit-scrollbar { display: none; }
          .si-nav-item {
            white-space: nowrap;
            width: auto;
            font-size: 12.5px;
            padding: 8px 14px;
            border: 1px solid transparent;
          }
          .si-nav-item.active {
            border-color: #d2d2d7;
            background: #fff;
          }
          .si-modal-body { padding: 16px; }
          .si-modal-header { padding: 16px 20px; }
          .si-modal-footer {
            padding: 12px 20px;
            background: #fff;
            border-top: 1px solid #e8e8ed;
          }
          .si-roster-scroll { max-height: calc(100vh - 350px); }
        }

        @media (max-width: 480px) {
          .si-header { flex-direction: column; align-items: stretch; gap: 12px; }
          .si-btn-primary { width: 100%; justify-content: center; }
          .si-modal-footer { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
          .si-btn-cancel, .si-btn-submit { width: 100%; min-width: 0; margin: 0; text-align: center; }
        }
      `}</style>
    </div>
  );
};

export default SessionInfo;